import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Eye, Sparkles, Lock, ArrowLeft, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MONTH_NAMES_PT = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];

function daysInMonth(year, month) {
  return new Date(year, month, 0).getDate();
}

export default function MemberSpiritualAssessor() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [shared, setShared] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [selected, setSelected] = useState(null);  // member id
  const [schedule, setSchedule] = useState(null);
  const [loadingSchedule, setLoadingSchedule] = useState(false);

  const dim = daysInMonth(year, month);
  const days = useMemo(() => Array.from({ length: dim }, (_, i) => i + 1), [dim]);

  const loadShared = async () => {
    setLoadingList(true);
    try {
      const r = await axios.get(`${API}/members/spiritual/assessor/shared`, { withCredentials: true });
      setShared(r.data.items || []);
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error("Acesso reservado ao Assessor Espiritual.");
      } else {
        toast.error("Erro ao carregar.");
      }
      setShared([]);
    } finally { setLoadingList(false); }
  };

  const loadSchedule = async (memberId, y = year, m = month) => {
    setLoadingSchedule(true);
    try {
      const r = await axios.get(
        `${API}/members/spiritual/assessor/schedule/${memberId}?year=${y}&month=${m}`,
        { withCredentials: true },
      );
      setSchedule(r.data);
    } catch (e) {
      if (e?.response?.status === 404) toast.error("Este sócio já não partilha consigo.");
      else toast.error("Erro ao carregar horário.");
      setSchedule(null);
    } finally { setLoadingSchedule(false); }
  };

  useEffect(() => { loadShared(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps
  // loadSchedule takes explicit args; deps below are the real triggers.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (selected) loadSchedule(selected, year, month); }, [selected, year, month]);

  const prevMonth = () => { if (month === 1) { setYear((y) => y - 1); setMonth(12); } else setMonth((m) => m - 1); };
  const nextMonth = () => { if (month === 12) { setYear((y) => y + 1); setMonth(1); } else setMonth((m) => m + 1); };

  const back = () => { setSelected(null); setSchedule(null); };

  const downloadPdf = async () => {
    if (!selected) return;
    try {
      const r = await axios.get(
        `${API}/members/spiritual/assessor/schedule/${selected}/pdf?year=${year}&month=${month}`,
        { withCredentials: true, responseType: "blob" },
      );
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `resumo-espiritual-${year}-${String(month).padStart(2, "0")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Erro a gerar o PDF.");
    }
  };

  if (loadingList) return <section className="container-page py-12"><p className="text-[hsl(var(--muted-foreground))]">A carregar…</p></section>;

  if (!selected) {
    return (
      <section className="container-page py-10 lg:py-14" data-testid="assessor-shared-list-page">
        <div className="overline flex items-center gap-2"><Sparkles className="h-3 w-3" /> Assessor Espiritual</div>
        <h1 className="display-heading text-3xl sm:text-4xl mt-3">Acompanhamento Espiritual.</h1>
        <span className="accent-rule mt-5 block" />
        <p className="mt-4 max-w-2xl text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">
          Sócios que escolheram partilhar consigo o seu Horário Espiritual e Exame Particular. Acesso estritamente apenas-leitura.
        </p>

        {shared.length === 0 ? (
          <div className="mt-10 bg-white border border-[hsl(var(--border))] p-6 lg:p-8" data-testid="assessor-empty">
            <div className="overline">Sem partilhas</div>
            <p className="mt-3 text-sm text-[hsl(var(--muted-foreground))]">
              Nenhum sócio escolheu partilhar consigo até ao momento.
            </p>
          </div>
        ) : (
          <ul className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="assessor-shared-list">
            {shared.map((m) => (
              <li key={m.id} className="bg-white border border-[hsl(var(--border))] p-5 hover:border-[hsl(var(--accent))] transition-colors" data-testid={`assessor-shared-${m.id}`}>
                <div className="flex items-center gap-3">
                  {m.picture ? (
                    <img src={m.picture} alt={m.name} className="h-12 w-12 rounded-full object-cover" />
                  ) : (
                    <div className="h-12 w-12 rounded-full bg-[hsl(var(--secondary))] flex items-center justify-center text-base font-medium">
                      {(m.name || "?").slice(0, 1).toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm truncate">{m.name}</div>
                    <div className="text-[11px] text-[hsl(var(--muted-foreground))] truncate">{m.email}</div>
                  </div>
                </div>
                <button onClick={() => setSelected(m.id)}
                  className="mt-5 w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-[hsl(var(--primary))] text-white text-xs uppercase tracking-[0.2em] hover:bg-[hsl(var(--primary))]/90"
                  data-testid={`assessor-view-${m.id}`}>
                  <Eye className="h-3.5 w-3.5" /> Ver horário
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    );
  }

  // Detail view
  return (
    <section className="container-page py-10 lg:py-14" data-testid="assessor-schedule-page">
      <button onClick={back} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]" data-testid="assessor-back">
        <ArrowLeft className="h-3.5 w-3.5" /> Voltar à lista
      </button>
      <div className="overline mt-6 flex items-center gap-2"><Lock className="h-3 w-3" /> Apenas leitura · Partilhado consigo</div>
      <h1 className="display-heading text-3xl sm:text-4xl mt-2">{schedule?.member?.name || "Sócio"}</h1>
      <span className="accent-rule mt-5 block" />
      {schedule?.member?.ideal_pessoal && (
        <p className="mt-4 max-w-2xl text-sm italic text-[hsl(var(--muted-foreground))] leading-relaxed">
          “{schedule.member.ideal_pessoal}”
        </p>
      )}

      {/* Month nav */}
      <div className="mt-8 flex items-center gap-3 flex-wrap">
        <button onClick={prevMonth} className="h-10 w-10 inline-flex items-center justify-center border border-[hsl(var(--border))] hover:border-[hsl(var(--accent))]" data-testid="assessor-prev-month">
          <ChevronLeft className="h-4 w-4" />
        </button>
        <div className="display-heading text-2xl">{MONTH_NAMES_PT[month - 1]} {year}</div>
        <button onClick={nextMonth} className="h-10 w-10 inline-flex items-center justify-center border border-[hsl(var(--border))] hover:border-[hsl(var(--accent))]" data-testid="assessor-next-month">
          <ChevronRight className="h-4 w-4" />
        </button>
        <button onClick={downloadPdf}
          className="ml-auto inline-flex items-center gap-2 px-4 py-2 border border-[hsl(var(--primary))] text-[hsl(var(--primary))] text-xs uppercase tracking-[0.2em] hover:bg-[hsl(var(--primary))] hover:text-white transition-colors"
          data-testid="assessor-download-pdf">
          <Download className="h-3.5 w-3.5" /> Resumo PDF
        </button>
      </div>

      {loadingSchedule || !schedule ? (
        <p className="mt-10 text-sm text-[hsl(var(--muted-foreground))]">A carregar horário…</p>
      ) : (
        <>
          {/* Exame Particular */}
          <div className="mt-10 bg-white border border-[hsl(var(--border))] p-6 lg:p-8" data-testid="assessor-exame">
            <div className="overline">Exame particular</div>
            {schedule.exame_particular ? (
              <p className="mt-4 text-sm whitespace-pre-wrap leading-relaxed">{schedule.exame_particular}</p>
            ) : (
              <p className="mt-4 text-xs italic text-[hsl(var(--muted-foreground))]">Sem registo neste mês.</p>
            )}
          </div>

          {/* Vínculos read-only */}
          <div className="mt-10 space-y-10">
            {schedule.vinculos.map((v) => (
              <div key={v.key} className="bg-white border border-[hsl(var(--border))] p-6 lg:p-8">
                <h3 className="display-heading text-xl text-[hsl(var(--primary))]">{v.label}</h3>
                {v.practices.length === 0 ? (
                  <p className="mt-4 text-xs italic text-[hsl(var(--muted-foreground))]">Sem práticas registadas.</p>
                ) : (
                  <div className="mt-5 overflow-x-auto">
                    <table className="min-w-full text-xs">
                      <thead>
                        <tr className="border-b border-[hsl(var(--border))]">
                          <th className="text-left py-2 pr-3 sticky left-0 bg-white z-10 min-w-[200px]">Prática</th>
                          {days.map((d) => (
                            <th key={d} className="px-1 py-2 text-center font-normal text-[hsl(var(--muted-foreground))]">{d}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {v.practices.map((p) => (
                          <tr key={p.id} className="border-b border-[hsl(var(--border))] last:border-0">
                            <td className="py-2 pr-3 align-middle sticky left-0 bg-white z-10 min-w-[200px]">{p.title}</td>
                            {days.map((d) => {
                              const val = p.days?.[d] || "";
                              return (
                                <td key={d} className="px-0.5 py-1 text-center">
                                  <div className={`h-6 w-6 mx-auto inline-flex items-center justify-center border text-[10px] ${
                                    val === "done" ? "bg-[hsl(var(--primary))] text-[hsl(var(--accent))] border-[hsl(var(--primary))]" :
                                    val === "skip" ? "bg-[hsl(var(--secondary))] text-[hsl(var(--muted-foreground))] border-[hsl(var(--border))]" :
                                    "bg-white border-[hsl(var(--border))]"
                                  }`}>
                                    {val === "done" ? "✓" : val === "skip" ? "·" : ""}
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Dashboard */}
          <div className="mt-10 bg-[hsl(var(--primary))] text-white p-6 lg:p-8" data-testid="assessor-dashboard">
            <div className="overline text-[hsl(var(--accent))]"><Sparkles className="inline h-3 w-3 mr-1" /> Cumprimento — {MONTH_NAMES_PT[month - 1]}</div>
            <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {(schedule.dashboard?.by_vinculo || []).map((d) => {
                const pct = Math.round((d.completion || 0) * 100);
                return (
                  <div key={d.key} className="border border-white/15 p-4">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-white/70">{d.label}</div>
                    <div className="mt-2 text-3xl tabular-nums">{pct}%</div>
                    <div className="text-[11px] text-white/60 mt-1">{d.done_days} de {d.marked_days} previstos</div>
                    <div className="mt-3 h-1 bg-white/15">
                      <div className="h-1 bg-[hsl(var(--accent))]" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </section>
  );
}
